import { gql, useMutation, useLazyQuery, useQuery } from "@apollo/client";
import * as xlsx from "xlsx";
import { useState, useEffect } from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Typography, Box, TextField, Button, Autocomplete } from "@mui/material";
import { FlexBetween } from "../../commonStyles";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { useAppDispatch } from "../../app/hooks";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { handleModal } from "../../features/modal/modalSlice";
import { numToCurrency, handleTextReplacement, keyToCamel } from "../../utils/function";
import { useNavigate } from "react-router";
import { useTextTemplatesQuery } from "../../features/textTemplate/textTemplateHooks";
import { selectTextTemplates } from "../../features/textTemplate/textTemplateSlice";
import { ITextTemplate } from "../../types/textTemplate";
import { IProject } from "../../types/project";
import { IUser } from "../../types/user";

const MassText = (props: ChildProps) => {
  const { id, selectedRegistrants, project } = props;
  const storeDispatch = useAppDispatch();
  const navigate = useNavigate();
  const auth = useSelector(selectUser);
  const textTemplates = useSelector(selectTextTemplates);
  const [name, setName] = useState<string>("");
  const [textTemplate, setTextTemplate] = useState<ITextTemplate | null>(null);
  const [previewText, setPreviewText] = useState<string>("");
  const [textDate, setTextDate] = useState<Date | null>(null);
  const [customRegistrants, setCustomRegistrants] = useState<ICustomRegistrant[]>([]);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(project ? project : null);
  const [phoneOptions, setPhoneOptions] = useState<IPhoneOption[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<IPhoneOption | null>(null);

  useQuery(GETPHONEOPTIONS, {
    variables: { filter: { OR: [{ type: "Sales" }, { type: "Manager" }], locked: false } },
    onCompleted: (data) => {
      setProjects(data.projectMany);
      const projectPhones = data.projectMany
        .filter((project: IProject) => project.trackingPhone)
        .map((project: IProject) => ({ _id: project._id, name: project.name, phone: project.trackingPhone!.toString(), type: "Project" }));
      const userPhones = data.userMany
        .filter((crmUser: IUser) => crmUser.trackingPhone)
        .map((crmUser: IUser) => ({ _id: crmUser._id, name: crmUser.fullName, phone: crmUser.trackingPhone!.toString(), type: "User" }));
      setPhoneOptions([...projectPhones, ...userPhones]);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [getTexts] = useTextTemplatesQuery(1, 100, "_ID_DESC");

  const [saveTextSchedule] = useMutation(SAVETEXTSCHEDULE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar("Scheduled Text Saved!"));
      storeDispatch(handleModal(false));
      navigate(`/dashboard`);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar("Access Forbidden"));
    },
  });

  const [getRegistrant] = useLazyQuery(GETREGISTRANT, {
    onError: (err) => {
      console.log(err, "err");
    },
  });

  useEffect(() => {
    getTexts();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!textTemplate) return setPreviewText("");
    const templateProject = textTemplate.project ? textTemplate.project : selectedProject!;
    if (selectedRegistrants.length) {
      getRegistrant({ variables: { _id: selectedRegistrants[0] } }).then((res) => {
        const registrant = res.data?.registrantById;
        setPreviewText(registrant ? handleTextReplacement(textTemplate.text, registrant, auth!, templateProject) : textTemplate.text);
      });
    } else if (customRegistrants.length) {
      setPreviewText(
        handleTextReplacement(
          textTemplate.text,
          { ...customRegistrants[0], addresses: [customRegistrants[0].address] },
          auth!,
          templateProject
        )
      );
    } else {
      setPreviewText(textTemplate.text);
    }
    // eslint-disable-next-line
  }, [textTemplate, customRegistrants, selectedRegistrants]);

  const readUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files) {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        const data = event.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        let customData = keyToCamel([...json]).map((data: any) => {
          return {
            firstName: data.firstName ? data.firstName : "",
            phone: data.phone ? data.phone.toString() : "",
            address: data.address ? data.address : "",
            suite: data.suite ? data.suite : "",
          };
        });
        setCustomRegistrants(customData);
      };
      reader.readAsArrayBuffer(event.target.files[0]);
    }
  };

  const recipientCount = id === "custom" ? customRegistrants.length : selectedRegistrants.length;
  const { length: smsLength, segments, encoding } = getSmsSegments(previewText || textTemplate?.text || "");

  const handleSmsSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!textDate) return storeDispatch(showErrorSnackbar("Date for Mass Mail is required"));
    if (!textTemplate) return storeDispatch(showErrorSnackbar("Template is required"));
    if (!selectedProject) return storeDispatch(showErrorSnackbar("Project is required"));
    if (!selectedPhone) return storeDispatch(showErrorSnackbar("Phone is required"));
    if (id === "custom" && !customRegistrants.length) return storeDispatch(showErrorSnackbar("No Excel File Found"));

    if (id === "custom") {
      if (!customRegistrants.every((custom: any) => custom.phone))
        return storeDispatch(showErrorSnackbar("There are missing phones in the uploaded excel file or the columns are incorrect"));
    }

    let allRegistrants = selectedRegistrants;

    let template = textTemplates.find((template: ITextTemplate) => template._id === textTemplate._id);

    if (template) {
      saveTextSchedule({
        variables: {
          record: {
            project: selectedProject._id,
            phone: parseInt(selectedPhone.phone, 10),
            name: name,
            textTemplate: textTemplate._id,
            user: auth?._id,
            textDate,
            count: id === "custom" ? customRegistrants.length : selectedRegistrants.length,
            type: id === "custom" ? "custom" : id === "purchasers" ? "purchaser" : "registrant",
            registrants: id === "custom" ? [] : id === "purchasers" ? [] : allRegistrants,
            purchasers: id === "purchasers" ? allRegistrants : [],
            custom: id === "custom" ? customRegistrants : [],
          },
        },
      });
    }
  };

  return (
    <div>
      <FlexBetween>
        <Typography sx={{ alignSelf: "center" }} variant="h2" component="div" gutterBottom>
          <strong>Send Mass Text</strong>
        </Typography>
        <Typography sx={{ alignSelf: "center" }} variant="h3" component="div" gutterBottom>
          <strong>
            Selected:{" "}
            <Box sx={{ display: "inline", color: "success.main" }}>
              {selectedRegistrants && selectedRegistrants.length ? selectedRegistrants.length : customRegistrants.length}
            </Box>
          </strong>
        </Typography>
      </FlexBetween>
      {id === "custom" ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ my: 1 }}>You can upload a custom excel. It will require two columns (firstName and phone). phone is mandatory.</Box>
          <input style={{ display: "none" }} id="upload-excel" name="upload-excel" onChange={(e) => readUploadFile(e)} type="file" />
          <label htmlFor="upload-excel">
            <Button color="primary" variant="contained" component="span">
              Upload Excel
            </Button>
          </label>
        </Box>
      ) : null}
      <Box>
        <strong>
          Approximate Cost:{" "}
          <Box sx={{ display: "inline", color: "error.main" }}>
            {numToCurrency.format(segments * recipientCount * SMS_SEGMENT_PRICE)} USD
          </Box>
        </strong>
        {textTemplate ? (
          <Box sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
            {smsLength} characters ({encoding}) = {segments} segment{segments === 1 ? "" : "s"} x {recipientCount} recipient
            {recipientCount === 1 ? "" : "s"} x {numToCurrency.format(SMS_SEGMENT_PRICE)}
          </Box>
        ) : null}
      </Box>
      <form onSubmit={handleSmsSubmit}>
        <Box sx={{ my: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="SMS Date"
              value={textDate}
              minDate={new Date()}
              disablePast
              onChange={(newValue) => {
                setTextDate(newValue);
              }}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <TextField
            title={"Name"}
            name={"name"}
            fullWidth
            value={name}
            label={"Name"}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            id="text-project"
            options={projects}
            value={selectedProject}
            isOptionEqualToValue={(option: IProject, value: IProject) => option._id === value._id}
            getOptionLabel={(option: IProject) => `${option.name}`}
            onChange={(e, value) => {
              setSelectedProject(value);
              if (value && selectedPhone?.type === "Project" && selectedPhone._id !== value._id) setSelectedPhone(null);
            }}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Project" required />}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            id="text-phone"
            options={phoneOptions.filter(
              (option: IPhoneOption) => option.type === "User" || !selectedProject || option._id === selectedProject._id
            )}
            value={selectedPhone}
            groupBy={(option: IPhoneOption) => option.type}
            isOptionEqualToValue={(option: IPhoneOption, value: IPhoneOption) => option.phone === value.phone && option.name === value.name}
            getOptionLabel={(option: IPhoneOption) => `${option.name} - ${option.phone}`}
            onChange={(e, value) => setSelectedPhone(value)}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Send From (Phone)" required />}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            id="email-templates"
            options={textTemplates}
            freeSolo={false}
            getOptionLabel={(option: any) => `${option.name}`}
            onChange={(e, value) => setTextTemplate(value)}
            renderInput={(params) => <TextField {...params} variant="outlined" label="SMS Templates" />}
          />
        </Box>
        {textTemplate ? (
          <Box sx={{ my: 1 }}>
            <strong>Sample SMS Template</strong>
            <Box sx={{ my: 2 }}>
              <iframe
                title={`${textTemplate.name}`}
                style={{ width: "100%", margin: 0, padding: 0, height: "150px", display: "block", border: "0px" }}
                srcDoc={previewText}
                loading="lazy"
              />
            </Box>
          </Box>
        ) : null}
        <FlexBetween sx={{ mt: 2 }}>
          <Button type="submit" color="success" variant="contained">
            Schedule
          </Button>
          <Button onClick={() => storeDispatch(handleModal(false))} color="info" variant="contained">
            Cancel
          </Button>
        </FlexBetween>
      </form>
    </div>
  );
};

interface ChildProps {
  id: string;
  selectedRegistrants: string[];
  project?: IProject | null;
}

interface ICustomRegistrant {
  firstName?: string;
  phone: string;
  address?: string;
}

// Twilio price per outbound SMS segment (USD)
const SMS_SEGMENT_PRICE = 0.0170;

// GSM-7 basic character set; anything outside this (or the extension table) forces UCS-2 encoding
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
// GSM-7 extension characters count as 2 characters each
const GSM_EXTENDED = "^{}\\[~]|€\f";

// Mirrors Twilio's segmenting: GSM-7 allows 160 chars in one segment (153 per segment when split),
// UCS-2 (e.g. emojis, smart quotes) allows 70 chars in one segment (67 per segment when split)
const getSmsSegments = (text: string) => {
  const chars = Array.from(text);
  if (!chars.length) return { length: 0, segments: 1, encoding: "GSM-7" };
  const isGsm = chars.every((char) => GSM_BASIC.includes(char) || GSM_EXTENDED.includes(char));
  if (isGsm) {
    const length = chars.reduce((total, char) => total + (GSM_EXTENDED.includes(char) ? 2 : 1), 0);
    return { length, segments: length <= 160 ? 1 : Math.ceil(length / 153), encoding: "GSM-7" };
  }
  // UCS-2 counts in UTF-16 code units, so emojis count as 2
  const length = text.length;
  return { length, segments: length <= 70 ? 1 : Math.ceil(length / 67), encoding: "Unicode" };
};

interface IPhoneOption {
  _id: string;
  name: string;
  phone: string;
  type: "Project" | "User";
}

const GETPHONEOPTIONS = gql`
  query phoneOptions($filter: FilterFindManyUserInput!) {
    projectMany(limit: 100000) {
      _id
      name
      trackingPhone
    }
    userMany(filter: $filter) {
      _id
      fullName
      trackingPhone
    }
  }
`;

const SAVETEXTSCHEDULE = gql`
  mutation textScheduleCreateOne($record: CreateOneTextScheduleInput!) {
    textScheduleCreateOne(record: $record) {
      record {
        _id
      }
    }
  }
`;

const GETREGISTRANT = gql`
  query registrantById($_id: MongoID!) {
    registrantById(_id: $_id) {
      _id
      firstName
      lastName
      alias
      fullName
      email
      primaryPhone
      addresses
    }
  }
`;

export default MassText;
